import { useMemo, useState } from "react";
import { Plus, ArrowRight, Copy, ChevronDown, Search } from "lucide-react";
import { Button, Card, CardEmpty, DarkHero, DarkStatTile, EmptyState, Field, Icon, Page, Skeleton } from "@cntrl/ui";
import { useCapability } from "../../app/bandContext.js";
import { useMediaObjectUrl } from "../../app/hooks/useMedia.js";
import { useReleases, type ReleaseSummary } from "../../app/hooks/useReleases.js";
import { useRelease, useSmartLinkStats } from "../../app/hooks/useRelease.js";
import { useTasks, type TaskSummary } from "../../app/hooks/useTasks.js";
import { PUBLIC_WEB_URL } from "../../config/env.js";
import { useTaskPeople, type TaskPersonRef } from "../tasks/taskPeople.js";
import { TaskListRow } from "../tasks/TaskListRow.js";
import { taskRowsClassName, taskSubLine, sortTasksForList } from "../tasks/taskRow.js";
import { ReleaseArtwork } from "./ReleaseArtwork.js";
import { ReleaseStatusPill } from "./ReleaseStatusPill.js";
import { ReleaseSettingsModal } from "./ReleaseSettingsModal.js";
import { TaskCreateModal } from "../tasks/TaskCreateModal.js";
import { SmartLinkStatTiles, DspClicksChart } from "./SmartLinkStats.js";
import type { ReleaseSectionKey } from "./sections.js";
import {
  catalogNewestFirst,
  catalogStat,
  countdownBlock,
  displayStatus,
  lifetimeClicks,
  pickFeatured,
  releaseMetaLine,
  releaseTypeLabel,
} from "./releaseFormat.js";
import styles from "./ReleasesScreen.module.scss";
import { usePhoneLayout } from "../../app/phoneLayout.js";

// The Releases HUB — four regions in order: header → featured banner → the two-up workspace
// (Release prep · Smart links) → the catalog (search + grid).
//
// Three rules govern the shape of this file:
//
//  1. **First run renders the FURNITURE, not a placeholder.** Zero releases still paints both workspace cards
//     with their own empty copy and the catalog's empty message INSIDE the grid — the banner is the only
//     region that disappears (it has nothing to feature). Replacing the whole screen with one EmptyState is
//     what we used to do, and it contradicts the born-blank contract the rest of the app follows.
//  2. **Analytics are ENRICHMENT, never a blocker.** The list never waits on them. The smart-link row is a
//     collapsed row you expand, so its stats query is lazy — a collapsed hub fires nothing, an open row fires
//     exactly one — which costs no backend.
//  3. **The catalog is a flat, NEWEST-FIRST grid with a search box** (ART-201, Ryan 2026-09-22). The lifecycle
//     filter pills (All / In cycle / Released / Drafts) were killed in favour of a right-aligned search box,
//     and the grid orders newest-first via `catalogNewestFirst` rather than by bucket.
//
// Role-gated to `releases:read`; a seat without it sees the access empty state. The `release-changed` signal
// repaints the whole screen, which is why the error branch is guarded on `data == null` — a failed BACKGROUND
// refetch keeps `data` populated in TanStack v5, and blanking a fully-painted hub for it is the defect the
// streaming pass corrected on 2026-08-21.

export interface ReleasesScreenProps {
  onOpenRelease: (releaseId: string, section?: ReleaseSectionKey) => void;
  onNewRelease: () => void;
}

/** Non-terminal task statuses count as "open" for the Release Prep panel + spotlight checklist context. */
const OPEN_TASK = (t: TaskSummary) => t.status !== "done" && t.status !== "approved";

export function ReleasesScreen({ onOpenRelease, onNewRelease }: ReleasesScreenProps) {
  const canRead = useCapability("releases:read");
  const [query, setQuery] = useState("");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [createTaskOpen, setCreateTaskOpen] = useState(false);
  const canWrite = useCapability("releases:write");
  // Creating a release and managing release settings are EDITOR/management actions — desktop-only on mobile
  // (spec §3: "mobile = catalogue + per-release stats, no editor"), the same line the per-release builder is
  // gated on. Below the shell swap (`lg`) the catalogue is read-only (ART-153 pass two).
  const isDesktop = !usePhoneLayout();

  const all = useReleases();
  const releases = useMemo(() => all.data?.items ?? [], [all.data]);
  // Newest-first (ART-201): the flat catalog order, most recent release date at the top, undated drafts
  // leading. Page-scoped — the list is a `created_on DESC` keyset (the hub fetches 60), so this orders the
  // loaded page and a catalogue past one page needs the ordering server-side.
  const ordered = useMemo(() => catalogNewestFirst(releases), [releases]);

  // The spotlight = the reference's `pickFeatured()` — the soonest in-cycle release, else the most recent
  // released one. Its composed detail feeds assets x/y + checklist % (the summary carries neither); it's also
  // the source of the band slug for the Smart Links URLs.
  const spotlight = useMemo(() => pickFeatured(releases), [releases]);
  const spotlightDetail = useRelease(spotlight?.releaseId);
  // The band slug (constant per band) for building public smart-link URLs — from any loaded composed release.
  const slugSourceId = spotlight?.releaseId ?? releases[0]?.releaseId;
  const slugSource = useRelease(slugSourceId);
  const bandSlug = spotlightDetail.data?.data?.smartLink?.bandSlug ?? slugSource.data?.data?.smartLink?.bandSlug;

  const tasks = useTasks();

  if (!canRead) {
    return (
      <Page as="section">
        <EmptyState
          title="Releases aren't part of your access"
          description="Ask an owner or manager if you need the Releases workspace."
        />
      </Page>
    );
  }

  // The search box replaces the lifecycle filters: a case-insensitive title match over the newest-first list.
  const q = query.trim().toLowerCase();
  const filtered = q ? ordered.filter((r) => (r.title ?? "").toLowerCase().includes(q)) : ordered;

  return (
    <Page
      as="section"
      title="Releases"
      actions={
        isDesktop ? (
          <>
            {/* The to-do template every new release is stamped with, and — moved in from this header — the
                Spotify importer. Gated on releases:write: both change what future releases are born with.
                Desktop-only: creating/configuring a release is an editor action (spec §3). */}
            {canWrite && (
              <Button variant="ghost" onClick={() => setSettingsOpen(true)}>
                Release settings
              </Button>
            )}
            <Button onClick={onNewRelease}>
              <Icon icon={Plus} size={16} /> New release
            </Button>
          </>
        ) : undefined
      }
    >
      {all.isLoading ? (
        <div className={styles.skeletons} role="status" aria-live="polite" aria-label="Loading releases">
          <Skeleton variant="tile" />
          <Skeleton variant="line" width="70%" />
          <Skeleton variant="line" width="45%" />
        </div>
      ) : all.isError && all.data == null ? (
        <EmptyState
          title="We couldn't load your releases"
          description={all.error instanceof Error && all.error.message ? all.error.message : "Please try again."}
          action={<Button onClick={() => void all.refetch()}>Retry</Button>}
        />
      ) : (
        <>
          {/* The banner renders NOTHING when there is nothing to feature — never an empty shell. */}
          {spotlight && (
            <Spotlight
              release={spotlight}
              assets={spotlightDetail.data?.data?.assets?.progress}
              checklist={spotlightDetail.data?.data?.toDo}
              // All three come off the composed detail this banner already loads, so the meta line's track
              // count and label — and the ad-spend tile — cost no extra request and no widening of the hub's
              // list DTO. `budget` is null for a seat without money:marketing; the tile dashes.
              trackCount={spotlightDetail.data?.data?.tracks?.length}
              labelName={spotlightDetail.data?.data?.overview?.labelName}
              adSpend30dCents={spotlightDetail.data?.data?.budget?.adSpend30dCents}
              onOpen={() => onOpenRelease(spotlight.releaseId as string)}
            />
          )}

          <div className={styles.panels}>
            <ReleasePrepPanel
              releases={releases}
              tasks={tasks.data?.items ?? []}
              directory={tasks.data?.people ?? undefined}
              loading={tasks.isLoading}
              onAddTask={() => setCreateTaskOpen(true)}
              onOpenRelease={onOpenRelease}
            />
            <SmartLinksPanel releases={releases} bandSlug={bandSlug ?? undefined} onOpenRelease={onOpenRelease} />
          </div>

          {/* The catalog's control row: a right-aligned search box (ART-201). It replaced the four lifecycle
              filter pills — Ryan's "kill these buttons and replace it with a search bar". The label is hidden
              (the placeholder + the search icon carry it) but kept for the accessibility tree. */}
          <div className={styles.catalogBar}>
            <Field
              className={styles.search}
              type="search"
              label="Search releases"
              hideLabel
              placeholder="Search releases"
              affixStart={<Icon icon={Search} size={16} />}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </div>

          {/* The catalog's empty message lives INSIDE the grid, so the region keeps its shape on a first run
              instead of the screen swapping to a placeholder. Labelled so the grid is a nameable region — for
              a screen reader and for the test that proves the empty copy is inside it rather than replacing it. */}
          <div className={styles.grid} role="group" aria-label="Release catalog">
            {filtered.length === 0 ? (
              <p className={styles.gridEmpty}>
                {releases.length === 0
                  ? "No releases yet."
                  : `No releases match “${query.trim()}”.`}
              </p>
            ) : (
              filtered.map((r) => (
                <ReleaseCard key={r.releaseId} release={r} onOpen={() => onOpenRelease(r.releaseId as string)} />
              ))
            )}
          </div>
        </>
      )}

      {settingsOpen && <ReleaseSettingsModal onClose={() => setSettingsOpen(false)} />}
      {createTaskOpen && <TaskCreateModal onClose={() => setCreateTaskOpen(false)} />}
    </Page>
  );
}

// ---- spotlight -----------------------------------------------------------------------------------------

function Spotlight({
  release,
  assets,
  checklist,
  trackCount,
  labelName,
  adSpend30dCents,
  onOpen,
}: {
  release: ReleaseSummary;
  assets?: { total?: number; added?: number } | undefined;
  checklist?: { total?: number; doneCount?: number } | undefined;
  trackCount?: number | undefined;
  labelName?: string | null | undefined;
  adSpend30dCents?: number | null | undefined;
  onOpen: () => void;
}) {
  // One query for ONE release — the reference's own per-release enrichment, not an N+1 across the catalog.
  const stats = useSmartLinkStats(release.releaseId);

  // The banner's BACKGROUND is this release's cover art, washed across the full bleed — the reference sets it
  // inline (`coverBackground()` → `url(...) center / cover`) on the same `.rl-feat__bg` layer whose stylesheet
  // gradient is only the no-artwork fallback, so every release's banner is tinted by its own record. Ours was
  // the static spotlight gradient, identical for every release, which is the whole of what still read as "not
  // to spec" once the layout matched.
  //
  // Resolved through the SAME (mediaId, variant "sm") key `ReleaseArtwork` uses for its `tile` slot, so the
  // resolver cache serves both from one fetch — the hub's "at most two distinct renditions" test covers this.
  const cover = useMediaObjectUrl(release.coverMediaId ?? null, release.coverImageUrl ?? null, { variant: "sm" });
  const coverUrl = cover.error ? null : cover.url;
  const count = countdownBlock(release.releaseDate);
  const checklistPct =
    checklist && (checklist.total ?? 0) > 0
      ? Math.round(((checklist.doneCount ?? 0) / (checklist.total as number)) * 100)
      : null;
  // `AssetProgress.total` counts STORED rows and `CreateAsync` seeds none, so a fresh release reports 0 / 0 —
  // a WRONG number, not an empty one (audit R-5). The tile still RENDERS, showing the dash it owns: the
  // reference's stat row is four fixed slots each falling back to `-`, and a slot that disappears takes the
  // row's shape with it, so a fresh release looked like a different card rather than an unfilled one.
  const assetsTotal = assets?.total ?? 0;

  // The eyebrow follows the featured release's BUCKET, exactly as the reference does it ("Now in cycle ·
  // Single" / "Released · Single"): the banner can feature a shipped title when nothing is in cycle, and the
  // live dot means a live cycle, so it goes with the bucket rather than riding along on a past release.
  const bucket = displayStatus(release);
  const title = release.title || "Untitled release";

  return (
    <DarkHero
      className={styles.spotlight}
      // Absent ⇒ the shell keeps its own gradient, which is exactly the reference's fallback for a release with
      // no artwork.
      //
      // `bleed`, not the default sharp `scrim`. The reference's SHIPPED React pass hands the raw cover to a
      // 9:1 banner with no treatment at all, and it survives only because the artwork it was demoed on is dark
      // and already out of focus: a bright cover crops to a face across the card with the stat labels sitting
      // on it. The blurred wash is that same reference's OWN answer one revision earlier
      // (`releases-cc.css` — `__bg` blurred and over-scaled under a `__scrim`), dropped by the port rather
      // than decided against, and it is the treatment that reads the same whatever the band released.
      photoUrl={coverUrl}
      photoTreatment="bleed"
      // The reference's three-column banner: cover art, the lockup, the count block. The art is a COLUMN the
      // eyebrow and title sit beside — it is not a leading element of the body, which is what put the title on
      // its own full-width line above the artwork.
      media={
        <ReleaseArtwork
          seed={release.releaseId}
          mediaId={release.coverMediaId}
          imageUrl={release.coverImageUrl}
          size="tile"
          className={styles.spotlightArt}
          lazy={false}
        />
      }
      eyebrow={
        bucket === "in_cycle" ? (
          <>
            <span className={styles.liveDot} aria-hidden="true" /> Now in cycle · lead release ·{" "}
            {releaseTypeLabel(release.releaseType)}
          </>
        ) : (
          <>Released · {releaseTypeLabel(release.releaseType)}</>
        )
      }
      title={title}
      titleId="release-spotlight-title"
      action={
        <div className={styles.countdown}>
          {count.value !== null && <span className={styles.countdownNum}>{count.value}</span>}
          <span className={styles.countdownLabel}>{count.label}</span>
          {/* The visible affordance ONLY. `inert` keeps it out of the accessibility tree and out of the hit
              test, so the banner still exposes exactly one interactive element — the overlay below — while the
              pill stays the `Button` primitive rather than a hand-rolled one. It belongs to the count block
              (the reference puts it there), not to the body. */}
          <Button variant="ghost" inert tabIndex={-1} className={styles.spotlightOpen}>
            Open release <Icon icon={ArrowRight} size={16} />
          </Button>
        </div>
      }
    >
      <p className={styles.spotlightMeta}>{releaseMetaLine(release.releaseDate, trackCount, labelName)}</p>

      {/* `chrome="none"` — bare label+value on the hero's own gutter, which is what the reference's
          `.feat-stat` is: no fill, no hairline, no inset. Boxed chips read as "pills around the numbers", the
          same finding that unboxed the Streaming hero (ART-98) and the reason the tile has this prop. */}
      <div className={styles.spotlightStats}>
        <DarkStatTile
          label="Assets"
          value={assetsTotal > 0 ? (assets?.added ?? 0) : null}
          suffix={assetsTotal > 0 ? ` / ${assetsTotal}` : undefined}
          exact
          chrome="none"
        />
        <DarkStatTile label="Checklist" value={checklistPct} suffix="%" exact chrome="none" />
        {/* Lifetime clicks, summed from the per-DSP breakdown — the reference's own headline figure. The
            tile owns the null → "—" contract, so a failed read can never assert a fabricated 0. */}
        <DarkStatTile
          label="Smart-link clicks"
          value={lifetimeClicks(stats.data, stats.isError)}
          loading={stats.isLoading}
          exact
          chrome="none"
        />
        {/* Σ of the release's tagged Meta campaigns over the trailing 30 days, off the composed doc's gated
            budget block. Null for a seat without money:marketing AND for a band with no tagged campaign — both
            render the dash, which is correct for each: the figure is not this seat's to see, or there is none.
            It reads whole dollars because a hero tile beside "1 / 4" and "100%" is a headline, not a ledger. */}
        <DarkStatTile
          label="Ad spend · 30d"
          value={adSpend30dCents == null ? null : Math.round(adSpend30dCents / 100)}
          prefix="$"
          exact
          chrome="none"
        />
      </div>

      {/* One click target for the whole banner (the reference makes the card itself the link), as a real
          overlay element rather than a stretched-link ::after on the pill — see the SCSS for the measurement
          that killed that shape: `Button`'s hover lift re-parents its own pseudo-element and the overlay
          collapsed under the pointer. Last in the hero so it sits above the body without a z-index race. */}
      <button type="button" className={styles.spotlightHit} onClick={onOpen}>
        <span className={styles.srOnly}>Open release {title}</span>
      </button>
    </DarkHero>
  );
}

// ---- release prep panel --------------------------------------------------------------------------------

/**
 * Release prep — the hub's rollout triage: a FLAT list of every OPEN task that belongs to a release, overdue
 * first, then by due date.
 *
 * <b>Flat, NOT grouped under release heads (ART-201, Ryan 2026-09-22).</b> The panel used to render a header
 * row per release (artwork · title · "N open") with the tasks nested beneath it; Ryan's mockup drops those
 * heads and instead prepends the RELEASE NAME to each row's sub-line — "Same Side · Upload to BMI" — so the
 * release is named on the row it belongs to rather than in a group banner above it.
 *
 * <b>The rows are the app's shared task row (`TaskListRow`), not a shape of their own (ART-131's rule).</b>
 * The dashboard rail's anatomy, faces and overdue treatment. No `StatusPill`: urgency is the OVERDUE wash +
 * crimson rail, and at half-card width a third trailing column forces every title to ellipsize. The sub-line
 * is `taskSubLine(t)` with the release name KEPT (the default) — there is no group head naming it any more,
 * which is exactly the change: the release title moves from a banner onto the row's second line.
 */
function ReleasePrepPanel({
  releases,
  tasks,
  directory,
  loading,
  onAddTask,
  onOpenRelease,
}: {
  releases: ReleaseSummary[];
  tasks: TaskSummary[];
  /** The task read's own `people` block — the only source that can name an ORG assignee (ART-118). */
  directory?: TaskPersonRef[];
  loading: boolean;
  onAddTask: () => void;
  onOpenRelease: (releaseId: string, section?: ReleaseSectionKey) => void;
}) {
  // Resolved ONCE for the whole panel rather than per row — same cached seat list either way, but the rows
  // then cannot disagree with each other about who someone is.
  const people = useTaskPeople(directory);

  // Only the hub's OWN releases (a task's `releaseId` in the loaded catalogue), so a task pointing at a
  // release off this page is not surfaced with a name the panel cannot resolve.
  const releaseIds = useMemo(
    () => new Set(releases.map((r) => r.releaseId).filter(Boolean) as string[]),
    [releases],
  );

  // Flat and triage-sorted (overdue → due date → title): the mockup is one list, not per-release groups.
  const rows = useMemo(
    () => sortTasksForList(tasks.filter((t) => t.releaseId && releaseIds.has(t.releaseId) && OPEN_TASK(t))),
    [tasks, releaseIds],
  );

  return (
    <Card
      id="release-prep"
      className={styles.panelCard}
      title="Release prep"
      actions={
        <Button variant="ghost" size="sm" onClick={onAddTask}>
          <Icon icon={Plus} size={16} /> Add task
        </Button>
      }
    >
      {loading ? (
        <div className={styles.cardSkeletons} role="status" aria-live="polite" aria-label="Loading to-dos">
          <Skeleton variant="line" />
          <Skeleton variant="line" width="60%" />
        </div>
      ) : rows.length === 0 ? (
        // TWO different empty strings: "nothing to prep yet" and "everything is done" are different facts and
        // must not share one sentence.
        <CardEmpty>
          {releases.length === 0
            ? "No releases yet. Add one to start your rollout."
            : "All checklist items are checked off. Nice work."}
        </CardEmpty>
      ) : (
        <ul className={taskRowsClassName(styles.prepList)}>
          {rows.map((t) => (
            <TaskListRow
              key={t.taskId}
              task={t}
              people={people}
              // Release name PREPENDED (the default): "Same Side · Upload to BMI". There is no group head to
              // name it any more, so the row carries it.
              sub={taskSubLine(t)}
              onOpen={() => onOpenRelease(t.releaseId as string, "todo")}
            />
          ))}
        </ul>
      )}
    </Card>
  );
}

// ---- smart links panel ---------------------------------------------------------------------------------

function SmartLinksPanel({
  releases,
  bandSlug,
  onOpenRelease,
}: {
  releases: ReleaseSummary[];
  bandSlug: string | undefined;
  onOpenRelease: (releaseId: string, section?: ReleaseSectionKey) => void;
}) {
  // Newest at the top (ART-201), same order as the catalog grid — the server returns the list in an unrelated
  // order, so sort here rather than relying on it.
  const withSlug = catalogNewestFirst(releases).filter((r) => r.smartLinkSlug);
  return (
    <Card
      id="smart-links"
      className={styles.panelCard}
      title="Smart links"
      subtitle="Expand any release to copy, edit & read stats without opening it."
    >
      {withSlug.length === 0 ? (
        <CardEmpty>No public smart links yet, add a release to publish one.</CardEmpty>
      ) : (
        <ul className={styles.linkRows}>
          {withSlug.map((r) => (
            <SmartLinkRow key={r.releaseId} release={r} bandSlug={bandSlug} onOpenRelease={onOpenRelease} />
          ))}
        </ul>
      )}
    </Card>
  );
}

function SmartLinkRow({
  release,
  bandSlug,
  onOpenRelease,
}: {
  release: ReleaseSummary;
  bandSlug: string | undefined;
  onOpenRelease: (releaseId: string, section?: ReleaseSectionKey) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const url = publicSmartLinkUrl(bandSlug, release.smartLinkSlug);
  // LAZY: the hook's own `enabled` gate means a collapsed row fires no request at all, and an open row fires
  // exactly one. That is what makes per-release stats affordable on a hub with no batch enrichment read (G-2).
  const stats = useSmartLinkStats(expanded ? release.releaseId : undefined);
  // …and, on the same gate, the composed release — for its configured DSP buttons. Without them the chart's
  // row set differed from the builder's for the same release (step 04 showed a `0` row per button, the hub
  // showed "none yet"), and a loading/errored stats read left the chart with nothing to dash. Deduped by
  // React Query, so expanding the spotlight's own release is a cache hit, not a request.
  const detail = useRelease(expanded ? release.releaseId : undefined);

  async function copy() {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  }

  return (
    <li className={styles.linkRow}>
      <div className={styles.linkRowHead}>
        <ReleaseArtwork seed={release.releaseId} mediaId={release.coverMediaId}
          imageUrl={release.coverImageUrl} size="chip" />
        <span className={styles.linkTitle}>{release.title || "Untitled release"}</span>
        <Button variant="ghost" size="sm" onClick={() => void copy()} disabled={!url}>
          <Icon icon={Copy} size={16} /> {copied ? "Copied" : "Copy"}
        </Button>
        <button
          type="button"
          className={styles.linkExpand}
          aria-label={expanded ? "Collapse" : "Expand"}
          aria-expanded={expanded}
          data-open={expanded || undefined}
          onClick={() => setExpanded((v) => !v)}
        >
          <Icon icon={ChevronDown} size={16} />
        </button>
      </div>
      {expanded && (
        <div className={styles.linkDetail}>
          <p className={styles.linkUrl}>{url ?? "/…/" + (release.smartLinkSlug ?? "")}</p>
          <SmartLinkStatTiles stats={stats.data} isError={stats.isError} lifetime compact />
          <DspClicksChart
            dspLinks={detail.data?.data?.dspLinks ?? undefined}
            stats={stats.data}
            isError={stats.isError}
          />
          <div className={styles.linkFoot}>
            <Button variant="ghost" size="sm" onClick={() => onOpenRelease(release.releaseId as string, "smart-link")}>
              Edit links
            </Button>
          </div>
        </div>
      )}
    </li>
  );
}

// ---- release grid card ---------------------------------------------------------------------------------

function ReleaseCard({ release, onOpen }: { release: ReleaseSummary; onOpen: () => void }) {
  return (
    <button type="button" className={styles.card} onClick={onOpen}>
      <ReleaseArtwork
        seed={release.releaseId}
        mediaId={release.coverMediaId}
          imageUrl={release.coverImageUrl}
        size="card"
        className={styles.cardArt}
      >
        {/* The reference appends a track count for EP/Album only (`EP · 5`); `ReleaseSummary` carries no
            trackCount, so the chip ships the type alone rather than an N+1 per card (audit G-1). */}
        <span className={styles.cardType}>{releaseTypeLabel(release.releaseType)}</span>
      </ReleaseArtwork>
      <div className={styles.cardBody}>
        <span className={styles.cardTitle}>{release.title || "Untitled release"}</span>
        <ReleaseStatusPill status={displayStatus(release)} />
        <div className={styles.cardFoot}>
          {/* ONE stat slot, not the reference's two: the second is `N clicks`, which needs the hub
              enrichment read we do not have, and a column of em-dashes is worse than no column. */}
          <span>{catalogStat(release)}</span>
        </div>
      </div>
    </button>
  );
}

// ---- helpers -------------------------------------------------------------------------------------------

/** Build the absolute public smart-link URL, or undefined until the band slug resolves. */
function publicSmartLinkUrl(bandSlug: string | undefined, songSlug: string | null | undefined): string | undefined {
  if (!bandSlug || !songSlug) return undefined;
  const base = PUBLIC_WEB_URL || (typeof window !== "undefined" ? window.location.origin : "");
  return `${base}/${bandSlug}/${songSlug}`;
}
